import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import autosize from 'autosize'
import PropTypes from 'prop-types'
import React, { useRef, useEffect, useCallback, Children } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { useLazyGetUserSearchListQuery } from 'api/settings/getUserSearchList'
import { CommonTextLabel, CommonPhotoSelect } from 'components/common'
import TagsWrapper from 'components/customers/add/tab-content/TagsWrapper'
import TagsSelect from 'components/tags/TagsSelect'
import AvatarSquare from 'components/user/AvatarSquare'
import { getAccountMeta } from 'data/meta/accountMetaSelectors'
import { USER_SEARCH_PAGINATION } from 'settings/constants/pagination'
import T from 'T'
import { handleError } from 'utils/error'
import { combineAddressInfo, formatPhone } from 'utils/helper'
import { get } from 'utils/lodash'
import { memo } from 'utils/react'

import AdditionalNote from './AdditionalNote'

import './mention-style.scss'

const { INITIAL_PAGE, ROWS_PER_PAGE } = USER_SEARCH_PAGINATION

const CommonBody = ({ isOpenDrawer = false, details, isFollowUp, addAdditionalFollowUps, onChange }) => {
  const customerMeta = useSelector(getAccountMeta, shallowEqual)
  const subjectRef = useRef()
  const noteRef = useRef()

  const [getUserSearchList, { data: userSearchResult = [] }] = useLazyGetUserSearchListQuery()
  const userOptions = userSearchResult.users

  const loggedByUserImage = get(details, 'userInfo.profilePicUri', '')
  const loggedByFName = get(details, 'createdByFirstName', '')
  const loggedByLName = `${get(details, 'createdByLastName', '')}`
  const loggedByCompleteName = `${loggedByFName} ${loggedByLName}`.trim()

  const followUpNotes = get(details, 'followUpNotes', [])

  const metaTags = get(customerMeta, 'tags', [])
  const rawSelectedTags = get(details, 'tags', [])

  // Transform selectedTags to match TagsSelect expected format
  // If tags are returned as IDs, we need to find the full tag objects from metaTags
  const selectedTags = Array.isArray(rawSelectedTags)
    ? rawSelectedTags
        .map(tag => {
          // If tag is already an object with tagName, return as-is
          if (tag && typeof tag === 'object' && tag.tagName) {
            return tag
          }
          // If tag is just an ID or simple object, find it in metaTags
          const tagId = typeof tag === 'object' ? tag.id : tag
          return metaTags.find(metaTag => metaTag.id === tagId) || null
        })
        .filter(Boolean)
    : []

  const address = combineAddressInfo(
    get(details, 'location', '') ? get(details, 'location.address', {}) : get(details, 'account.billingAddress', {})
  )

  // Get assignee list
  const getAssigneeList = useCallback(
    (search = '') => {
      getUserSearchList({
        search,
        requestedPage: INITIAL_PAGE,
        requestedPageSize: ROWS_PER_PAGE,
      }).catch(handleError)
    },
    [getUserSearchList]
  )

  const handleSubjectChange = useCallback(event => onChange('subject', event.target.value), [onChange])

  const handleNoteChange = useCallback(event => onChange('note', event.target.value), [onChange])

  const handleTagsChange = useCallback((event, selectedOptions) => onChange('tags', selectedOptions), [onChange])

  const handleAssigneeChange = useCallback(
    (isChanged, value) => {
      if (isChanged) {
        onChange('assignee', value)
      }
      // TODO: For future don't call API for empty value, load from state directly just like Account rep in add customer
      getAssigneeList(isChanged ? '' : value)
    },
    [onChange, getAssigneeList]
  )

  useEffect(() => {
    autosize(subjectRef.current)
    autosize(noteRef.current)
  }, [])

  // The drawer stays mounted, so the call loads after autosize has already sized empty textareas
  useEffect(() => {
    autosize.update(subjectRef.current)
    autosize.update(noteRef.current)
  }, [details?.subject, details?.note])

  useEffect(() => {
    if (isOpenDrawer && isFollowUp) {
      // Call user assignee list only in case of followUp
      getAssigneeList()
    }
  }, [isOpenDrawer, isFollowUp, getAssigneeList])

  return (
    <div className="calls-content transparent-scroll">
      <div className="text-area-container subject">
        <textarea
          ref={subjectRef}
          className="common textarea subject-textarea"
          placeholder={T.SUBJECT}
          value={get(details, 'subject', '')}
          onChange={handleSubjectChange}
        />
      </div>

      <div className="call-details common-margin">
        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left" title={isFollowUp ? T.LOGGED_BY : T.ADDED_BY} />
          <Stack alignItems="center" className="ttc common-label label-right flex items-center" flexDirection="row">
            <AvatarSquare firstName={loggedByFName} lastName={loggedByLName} src={loggedByUserImage} />
            <Typography ml={1}>{loggedByCompleteName}</Typography>
          </Stack>
        </Stack>

        {isFollowUp && (
          <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
            <CommonTextLabel className="ttc common-label label-left" title={T.ASSIGNEE} />
            <Stack alignItems="center" className="ttc common-label label-right" flexDirection="row">
              <CommonPhotoSelect
                className="drawer-assignee-dropdown"
                placeholder={T.ASSIGNEE}
                selectedUser={get(details, 'assignee')}
                users={userOptions}
                onChange={handleAssigneeChange}
              />
            </Stack>
          </Stack>
        )}

        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left whitespace-nowrap" title={T.SERVICE_LOCATION} />
          <CommonTextLabel className="ttc common-label label-right" title={address} />
        </Stack>
        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left" title={T.CONTACT} />
          <CommonTextLabel
            className="ttc common-label label-right"
            title={`${get(details, 'contact.firstName', '')} ${get(details, 'contact.lastName', '')}`.trim()}
          />
        </Stack>
        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left" title={T.CONTACT_METHOD} />
          <CommonTextLabel className="ttc common-label label-right" title={formatPhone(get(details, 'contactMethod.methodValue', ''))} />
        </Stack>
        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left" title={T.DIRECTION} />
          <CommonTextLabel className="ttc common-label label-right" title={get(details, 'direction', '')} />
        </Stack>
        <Stack alignItems="center" flexDirection="row" sx={{ mb: 1 }}>
          <CommonTextLabel className="ttc common-label label-left" title={T.TAGS} />
          <Stack className="ttc common-label label-right" sx={{ width: '100%', '& .MuiAutocomplete-root': { width: '100%' } }}>
            <TagsWrapper>
              <TagsSelect
                key={(selectedTags || []).map(tag => tag.id).join(',')}
                deprecatedLabel={false}
                label={`${T.ACCOUNT_TAGS}`}
                limit={2}
                selectedTags={selectedTags}
                tags={metaTags.filter(data => data.active && data.forAccount)}
                onChange={handleTagsChange}
              />
            </TagsWrapper>
          </Stack>
        </Stack>
      </div>

      {!isFollowUp && (
        <>
          <CommonTextLabel className="ttc common-label label-left common-margin" title={T.CALL_NOTE} />
          <div className="call-note">
            <div className="full-note text-area-container">
              <textarea
                ref={noteRef}
                autoComplete="off"
                className="common textarea notes-textarea"
                name="note"
                placeholder={T.NOTE}
                value={get(details, 'note', '')}
                onChange={handleNoteChange}
              />
            </div>
          </div>
        </>
      )}

      {isFollowUp &&
        followUpNotes.length > 0 &&
        Children.toArray(
          followUpNotes.map((note, index) => {
            return (
              <>
                {index === 0 && <CommonTextLabel className="ttc common-label label-left common-margin" title={T.NOTE} />}
                <AdditionalNote addAdditionalFollowUps={addAdditionalFollowUps} index={index} note={note} onChange={onChange} />
              </>
            )
          })
        )}
    </div>
  )
}

CommonBody.defaultProps = {
  details: null,
  isFollowUp: false,
  addAdditionalFollowUps: null,
}

CommonBody.propTypes = {
  isOpenDrawer: PropTypes.bool,
  details: PropTypes.object,
  isFollowUp: PropTypes.bool,
  addAdditionalFollowUps: PropTypes.func,
  onChange: PropTypes.func.isRequired,
}

export default memo(CommonBody)
